/**
 * Renders i18n strings containing HTML tags (<br>, <strong>, <a>, etc.).
 *
 * scio1 i18n strings use HTML tags for formatting (rendered via Angular's ng-bind-html
 * with $sanitize). React escapes HTML by default, so these tags show as literal text.
 * This component sanitizes the HTML (allowing only safe tags) and renders it.
 *
 * Usage:
 *   <SafeHtml html={t('moveToGroupModalConfirm', { name })} />
 *
 * Equivalent to scio1's: <p ng-bind-html="'key' | translate"></p>
 *
 * Allowed tags: br, strong, em, b, i, a, span, div, p, ul, ol, li
 * All other tags (script, img, iframe, etc.) are stripped.
 * Event handler attributes (onclick, onerror, etc.) are stripped.
 */

/**
 * Escape HTML special characters in user-provided values before interpolation.
 * Call this on any dynamic value (folder names, user input) BEFORE passing
 * the i18n string to SafeHtml, so the regex sanitizer only handles known i18n tags.
 *
 * Usage: t('moveToGroupModalConfirm', { name: escapeHtml(folderName) })
 */
export function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const ALLOWED_TAGS = new Set([
  'br',
  'strong',
  'em',
  'b',
  'i',
  'a',
  'span',
  'div',
  'p',
  'ul',
  'ol',
  'li',
]);

const DANGEROUS_ATTR_PATTERN = /\s+on\w+\s*=/gi;
const DANGEROUS_URI_PATTERN = /href\s*=\s*["']?\s*(javascript|data):/gi;

/**
 * Lightweight HTML sanitizer — allows safe formatting tags, strips everything else.
 * Mirrors Angular's $sanitize behavior for our controlled i18n strings.
 *
 * Note: Designed for controlled i18n strings, not arbitrary user input.
 * For untrusted HTML, use a full sanitizer like DOMPurify.
 */
export function sanitizeHtml(html: string): string {
  return html.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)\b[^>]*>/g, (match, tag: string) => {
    const lower = tag.toLowerCase();
    if (!ALLOWED_TAGS.has(lower)) return '';
    // Strip event handlers (onclick, onerror, etc.) and dangerous URI schemes
    return match.replace(DANGEROUS_ATTR_PATTERN, ' ').replace(DANGEROUS_URI_PATTERN, 'href="');
  });
}

interface SafeHtmlProps {
  html: string;
  as?: keyof React.JSX.IntrinsicElements;
  className?: string;
}

/**
 * React equivalent of scio1's `ng-bind-html="'key' | translate"`.
 * Sanitizes HTML and renders it safely.
 */
export function SafeHtml({ html, as: Tag = 'span', className }: SafeHtmlProps) {
  const sanitized = sanitizeHtml(html);
  return <Tag className={className} dangerouslySetInnerHTML={{ __html: sanitized }} />;
}
